import React from 'react';
import { useTranslation, Trans } from 'react-i18next';
import imgOverview from '../../../assets/images/docs/draw-groups-overview.png';
import imgDrawTab from '../../../assets/images/docs/draw-groups-tab.png';
import { Body, MenuPath, Note, SectionDivider, Tip, Warning, YouTubeEmbed } from '../ui';

const DrawGroupsDoc: React.FC = () => {
  const { t } = useTranslation();

  return (
    <article className="font-body text-content-primary">
      {/* -- Intro -- */}
      <h2
        id="overview"
        className="font-display text-[24px] font-extrabold text-content-primary mb-[16px] tracking-[-0.4px]"
      >
        {t('docs.drawGroups.title')}
      </h2>
      <div className="my-[24px]">
        <img
          src={imgOverview}
          alt={t('docs.drawGroups.overviewImgAlt')}
          className="block max-w-full w-auto rounded-[10px] border border-surface-hover shadow-[0_2px_12px_rgba(34,211,238,0.08),0_1px_4px_rgba(0,0,0,0.06)]"
        />
      </div>
      <Body>{t('docs.drawGroups.intro1')}</Body>
      <Body>{t('docs.drawGroups.intro2')}</Body>
      <Body>{t('docs.drawGroups.intro3')}</Body>

      <YouTubeEmbed
        id="l7dR_-TunV4"
        label={t('docs.drawGroups.translateGroupsLabel')}
        title={t('docs.drawGroups.translateGroupsLabel')}
      />
      <YouTubeEmbed
        id="nlHCNFB8u2w"
        label={t('docs.drawGroups.rotateGroupsLabel')}
        title={t('docs.drawGroups.rotateGroupsLabel')}
      />

      <Note label={t('docs.drawGroups.noteLabel')}>
        {t('docs.drawGroups.introNote')}
      </Note>

      <SectionDivider />

      {/* -- The Draw Tab -- */}
      <h2
        id="draw-tab"
        className="font-display text-[24px] font-extrabold text-content-primary mb-[16px] tracking-[-0.4px]"
      >
        {t('docs.drawGroups.drawTabTitle')}
      </h2>
      <div className="my-[24px]">
        <img
          src={imgDrawTab}
          alt={t('docs.drawGroups.drawTabImgAlt')}
          className="block max-w-full w-auto rounded-[10px] border border-surface-hover shadow-[0_2px_12px_rgba(34,211,238,0.08),0_1px_4px_rgba(0,0,0,0.06)]"
        />
      </div>
      <Body>
        <Trans
          i18nKey="docs.drawGroups.drawTabBody"
          components={{ code: <MenuPath>{''}</MenuPath> }}
        />
      </Body>

      <h3 className="font-display text-[15px] font-bold text-content-primary mt-[20px] mb-[6px]">
        {t('docs.drawGroups.creatingTitle')}
      </h3>
      <Body>{t('docs.drawGroups.creatingBody')}</Body>
      <ul className="pl-[18px] mb-[16px] font-body text-[14px] text-content-primary leading-[1.7]">
        <li className="mb-[6px]">
          <Trans
            i18nKey="docs.drawGroups.creatingItemNew"
            components={{ strong: <strong /> }}
          />
        </li>
        <li className="mb-[6px]">
          <Trans
            i18nKey="docs.drawGroups.creatingItemAddSelected"
            components={{ strong: <strong /> }}
          />
        </li>
        <li className="mb-[6px]">
          <Trans
            i18nKey="docs.drawGroups.creatingItemRename"
            components={{ strong: <strong /> }}
          />
        </li>
        <li className="mb-[6px]">
          <Trans
            i18nKey="docs.drawGroups.creatingItemDelete"
            components={{ strong: <strong /> }}
          />
        </li>
      </ul>

      <SectionDivider />

      {/* -- Group properties -- */}
      <h2
        id="group-properties"
        className="font-display text-[24px] font-extrabold text-content-primary mb-[16px] tracking-[-0.4px]"
      >
        {t('docs.drawGroups.groupPropsTitle')}
      </h2>
      <Body>{t('docs.drawGroups.groupPropsBody')}</Body>

      <h3 className="font-display text-[15px] font-bold text-content-primary mt-[20px] mb-[6px]">
        {t('docs.drawGroups.modeTitle')}
      </h3>
      <Body>
        <Trans
          i18nKey="docs.drawGroups.modeBody"
          components={{ strong: <strong /> }}
        />
      </Body>

      <h3 className="font-display text-[15px] font-bold text-content-primary mt-[20px] mb-[6px]">
        {t('docs.drawGroups.strokeTypeTitle')}
      </h3>
      <Body>
        <Trans
          i18nKey="docs.drawGroups.strokeTypeBody"
          components={{ strong: <strong /> }}
        />
      </Body>

      <h3 className="font-display text-[15px] font-bold text-content-primary mt-[20px] mb-[6px]">
        {t('docs.drawGroups.poseIterationsTitle')}
      </h3>
      <Body>{t('docs.drawGroups.poseIterationsBody')}</Body>

      <h3 className="font-display text-[15px] font-bold text-content-primary mt-[20px] mb-[6px]">
        {t('docs.drawGroups.poseFromRestTitle')}
      </h3>
      <Body>{t('docs.drawGroups.poseFromRestBody')}</Body>

      <h3 className="font-display text-[15px] font-bold text-content-primary mt-[20px] mb-[6px]">
        {t('docs.drawGroups.linkHalvesTitle')}
      </h3>
      <Body>{t('docs.drawGroups.linkHalvesBody')}</Body>

      <SectionDivider />

      {/* -- Member properties -- */}
      <h2
        id="member-properties"
        className="font-display text-[24px] font-extrabold text-content-primary mb-[16px] tracking-[-0.4px]"
      >
        {t('docs.drawGroups.memberPropsTitle')}
      </h2>
      <Body>{t('docs.drawGroups.memberPropsBody')}</Body>

      <h3 className="font-display text-[15px] font-bold text-content-primary mt-[20px] mb-[6px]">
        {t('docs.drawGroups.axisLocksTitle')}
      </h3>
      <Body>
        <Trans
          i18nKey="docs.drawGroups.axisLocksBody"
          components={{ strong: <strong />, code: <MenuPath>{''}</MenuPath> }}
        />
      </Body>
      <Note label={t('docs.drawGroups.noteLabel')}>
        {t('docs.drawGroups.axisLocksNote')}
      </Note>

      <h3 className="font-display text-[15px] font-bold text-content-primary mt-[20px] mb-[6px]">
        {t('docs.drawGroups.referenceTitle')}
      </h3>
      <Body>
        <Trans
          i18nKey="docs.drawGroups.referenceBody"
          components={{ code: <MenuPath>{''}</MenuPath>, strong: <strong /> }}
        />
      </Body>

      <h3 className="font-display text-[15px] font-bold text-content-primary mt-[20px] mb-[6px]">
        {t('docs.drawGroups.leafAimTitle')}
      </h3>
      <Body>{t('docs.drawGroups.leafAimBody')}</Body>

      <h3 className="font-display text-[15px] font-bold text-content-primary mt-[20px] mb-[6px]">
        {t('docs.drawGroups.poseScaleTitle')}
      </h3>
      <Body>
        <Trans
          i18nKey="docs.drawGroups.poseScaleBody"
          components={{ strong: <strong /> }}
        />
      </Body>
      <Tip label={t('docs.drawGroups.tipLabel')}>
        {t('docs.drawGroups.poseScaleTip')}
      </Tip>

      <h3 className="font-display text-[15px] font-bold text-content-primary mt-[20px] mb-[6px]">
        {t('docs.drawGroups.poseOffsetTitle')}
      </h3>
      <Body>{t('docs.drawGroups.poseOffsetBody')}</Body>

      <SectionDivider />

      {/* -- Apply order -- */}
      <h2
        id="apply-order"
        className="font-display text-[24px] font-extrabold text-content-primary mb-[16px] tracking-[-0.4px]"
      >
        {t('docs.drawGroups.applyOrderTitle')}
      </h2>
      <Body>{t('docs.drawGroups.applyOrderBody1')}</Body>
      <Body>
        <Trans
          i18nKey="docs.drawGroups.applyOrderBody2"
          components={{ strong: <strong /> }}
        />
      </Body>

      <h3 className="font-display text-[15px] font-bold text-content-primary mt-[20px] mb-[6px]">
        {t('docs.drawGroups.applyOrderRuleTitle')}
      </h3>
      <Body>{t('docs.drawGroups.applyOrderRuleBody1')}</Body>
      <Body>
        <Trans
          i18nKey="docs.drawGroups.applyOrderRuleBody2"
          components={{ strong: <strong /> }}
        />
      </Body>
      <Tip label={t('docs.drawGroups.tipLabel')}>
        {t('docs.drawGroups.applyOrderRuleTip')}
      </Tip>

      <SectionDivider />

      {/* -- Mirroring -- */}
      <h2
        id="mirroring-draw-groups"
        className="font-display text-[24px] font-extrabold text-content-primary mb-[16px] tracking-[-0.4px]"
      >
        {t('docs.drawGroups.mirroringTitle')}
      </h2>
      <Body>{t('docs.drawGroups.mirroringBody')}</Body>

      <Warning label={t('docs.drawGroups.warningLabel')}>
        {t('docs.drawGroups.mirroringWarning')}
      </Warning>

      <SectionDivider />

      {/* -- Layers & save -- */}
      <h2
        id="layers-and-save"
        className="font-display text-[24px] font-extrabold text-content-primary mb-[16px] tracking-[-0.4px]"
      >
        {t('docs.drawGroups.layersSaveTitle')}
      </h2>
      <Body>{t('docs.drawGroups.layersSaveBody1')}</Body>
      <Body>
        <Trans
          i18nKey="docs.drawGroups.layersSaveBody2"
          components={{ code: <MenuPath>{''}</MenuPath> }}
        />
      </Body>
    </article>
  );
};

export default DrawGroupsDoc;
